import { useMemo, useState } from "react";

const PARAM_COLS = [
  ["ex_showroom", "Ex-Showroom"], ["kit", "Kit"], ["riao_finance", "RIAO Fin"], ["riao_cash", "RIAO Cash"],
  ["seat_cover", "Seat Cover"], ["side_bag", "Side Bag"], ["jaal", "Jaal"], ["helmet", "Helmet"],
  ["asd_discount", "ASD Disc"], ["customer_discount", "Cust Disc"],
];
const EST_COLS = [
  ["grand_total", "Grand Total"], ["extra_discount", "Extra Disc"], ["net_total", "Net Total"],
  ["cash", "Cash"], ["bank", "Bank"], ["advance", "Advance"], ["due_finance", "Due Finance"], ["due_customer", "Due Customer"],
];
const EST_LEVEL = new Set(["extra_discount", "net_total", "cash", "bank", "advance", "due_finance", "due_customer"]);

const inr = (v) => (v == null || v === 0 ? "—" : `${v < 0 ? "-" : ""}₹${Math.abs(Number(v)).toLocaleString("en-IN")}`);

export const PendingBackendTable = ({ items }) => {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");

  const rows = useMemo(() => items.filter((p) => {
    if (from && (!p.date || p.date < from)) return false;
    if (to && (!p.date || p.date > to)) return false;
    return true;
  }), [items, from, to]);

  const totals = useMemo(() => {
    const t = {};
    PARAM_COLS.forEach(([k]) => { t[k] = rows.reduce((s, r) => s + (Number(r.params?.[k]) || 0), 0); });
    t.grand_total = rows.reduce((s, r) => s + (Number(r.grand_total) || 0), 0);
    const seen = new Set();
    EST_COLS.forEach(([k]) => { if (EST_LEVEL.has(k)) t[k] = 0; });
    rows.forEach((r) => {
      if (seen.has(r.estimate_no)) return;
      seen.add(r.estimate_no);
      EST_COLS.forEach(([k]) => { if (EST_LEVEL.has(k)) t[k] += Number(r[k]) || 0; });
    });
    if (t.extra_discount) t.extra_discount = -Math.abs(t.extra_discount);
    return t;
  }, [rows]);

  const fmtD = (d) => (d ? d.split("-").reverse().join("-") : "—");
  const cellCls = (v) => `px-2.5 py-1.5 text-right ${v < 0 ? "text-rose-400" : "text-slate-300"}`;

  return (
    <div data-testid="pending-backend-table-panel" className="mt-1.5 rounded-lg border border-[#334155] bg-[#111827] overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 px-3 py-2 border-b border-slate-800">
        <span className="text-[9px] font-bold uppercase tracking-wide text-slate-400">Delivery Date</span>
        <input data-testid="pending-date-from" type="date" value={from} onChange={(e) => setFrom(e.target.value)}
          className="bg-[#1E293B] border border-[#334155] text-slate-200 rounded-md px-2 py-1 text-[11px]" />
        <span className="text-slate-600">→</span>
        <input data-testid="pending-date-to" type="date" value={to} onChange={(e) => setTo(e.target.value)}
          className="bg-[#1E293B] border border-[#334155] text-slate-200 rounded-md px-2 py-1 text-[11px]" />
        {(from || to) && (
          <button data-testid="pending-date-clear" onClick={() => { setFrom(""); setTo(""); }}
            className="text-[11px] text-blue-400 underline underline-offset-2">Clear</button>
        )}
        <span data-testid="pending-row-count" className="ml-auto rounded-full border border-blue-500/40 bg-blue-500/15 text-blue-300 px-3 py-0.5 text-[11px] font-bold">
          Rows: {rows.length} / {items.length}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table data-testid="pending-backend-table" className="text-[11px] whitespace-nowrap border-collapse min-w-full">
          <thead>
            <tr className="bg-[#0f172a] text-[9px] uppercase tracking-wide">
              <th className="px-2.5 py-1.5 text-left text-slate-400">#</th>
              <th className="px-2.5 py-1.5 text-left text-slate-400">Date</th>
              <th className="px-2.5 py-1.5 text-left text-slate-400">Chassis</th>
              <th className="px-2.5 py-1.5 text-left text-slate-400">Model</th>
              <th className="px-2.5 py-1.5 text-left text-slate-400">Customer</th>
              <th className="px-2.5 py-1.5 text-left text-slate-400">Estimate</th>
              {PARAM_COLS.map(([k, l]) => <th key={k} className="px-2.5 py-1.5 text-right text-cyan-300">{l}</th>)}
              {EST_COLS.map(([k, l]) => <th key={k} className="px-2.5 py-1.5 text-right text-indigo-300">{l}</th>)}
            </tr>
          </thead>
          <tbody>
            {rows.map((p, i) => (
              <tr key={p.chassis_no} data-testid={`pending-row-${p.chassis_no}`} className="border-t border-slate-800">
                <td className="px-2.5 py-1.5 text-slate-500">{i + 1}</td>
                <td className="px-2.5 py-1.5 text-slate-400">{fmtD(p.date)}</td>
                <td className="px-2.5 py-1.5 font-mono text-orange-400">{p.chassis_no}</td>
                <td className="px-2.5 py-1.5 text-slate-300">{p.model_name} {p.model_code && `(${p.model_code})`}</td>
                <td className="px-2.5 py-1.5 text-slate-400">{p.customer_name || "—"}</td>
                <td className="px-2.5 py-1.5 font-mono text-blue-400">{p.estimate_no}</td>
                {PARAM_COLS.map(([k]) => <td key={k} className={cellCls(p.params?.[k])}>{inr(p.params?.[k])}</td>)}
                <td className="px-2.5 py-1.5 text-right font-semibold text-emerald-400">{inr(p.grand_total)}</td>
                <td className={cellCls(-(p.extra_discount || 0))}>{inr(p.extra_discount ? -Math.abs(p.extra_discount) : null)}</td>
                <td className="px-2.5 py-1.5 text-right font-semibold text-emerald-400">{inr(p.net_total)}</td>
                <td className={cellCls(p.cash)}>{inr(p.cash)}</td>
                <td className={cellCls(p.bank)}>{inr(p.bank)}</td>
                <td className={cellCls(p.advance)}>{inr(p.advance)}</td>
                <td className={cellCls(p.due_finance)}>{inr(p.due_finance)}</td>
                <td className={cellCls(p.due_customer)}>{inr(p.due_customer)}</td>
              </tr>
            ))}
            {rows.length === 0 && (
              <tr><td colSpan={24} className="px-3 py-4 text-center text-slate-500">No chassis in selected date range</td></tr>
            )}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr data-testid="pending-totals-row" className="bg-[#0f172a] border-t-2 border-amber-500/40 font-extrabold text-amber-300">
                <td colSpan={6} className="px-2.5 py-2 text-[10px] uppercase tracking-wide text-amber-400">Totals · {rows.length} rows</td>
                {PARAM_COLS.map(([k]) => <td key={k} className="px-2.5 py-2 text-right">{inr(totals[k])}</td>)}
                {EST_COLS.map(([k]) => <td key={k} className="px-2.5 py-2 text-right">{inr(totals[k])}</td>)}
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};
